/**
 * Structural shape of a Z-report snapshot, as returned by both
 * `GET /api/v1/shifts/:id/z-report` (cashier) and
 * `GET /api/v1/admin/shifts/:id/z-report` (admin console).
 *
 * Declared here rather than imported from `@matix/api-client` so this package
 * stays dependency-free for any app that has its own client — the admin app
 * uses plain fetch. Both DTOs satisfy it structurally.
 */
/** One row of a breakdown: how many orders, and how much they came to. */
export interface ZReportBreakdownGroup {
  key: string;
  label: string;
  /** Arabic name, where the group has one (payment types). */
  label_ar?: string | null;
  count: number;
  /** Signed net — a returns row is negative. */
  total: number;
}

export interface ZReportPrintData {
  generated_at: string;
  printed_count: number;
  header: {
    shift_code: string;
    branch_name: string | null;
    cashier_name: string | null;
    drawer_name: string | null;
    opened_at: string;
    closed_at: string | null;
  };
  sales: {
    cash: number;
    card: number;
    wallet: number;
    credit: number;
    /** Tenant-specific methods outside the four named buckets. */
    other?: number;
    refunds_cash: number;
    discounts_total: number;
    vat_total: number;
    gross_sales: number;
    net_sales: number;
  };
  /**
   * How many receipts made those totals.
   *
   * Optional, and skipped rather than zeroed when absent: an admin console
   * printing against an API build that predates the block would otherwise put
   * "Orders 0" on a page reporting a full day of sales, which is worse than
   * saying nothing. Both live DTOs (the cashier's `ZReportDto` and the admin's)
   * always send it.
   */
  receipts?: {
    receipt_count: number;
    voided_count: number;
    /** Net sales ÷ receipts. Null when the shift rang nothing. */
    avg_ticket: number | null;
  };
  /**
   * The document-number span the shift covered — "invoices 40310 to 40312".
   *
   * One entry per receipt code, never merged: each code runs its own Entry_No
   * sequence, so a shift holding one return prints an invoice range AND a
   * return range rather than a single span bridging the two (which on a real
   * ponytail shift would read "272 → 40312"). Absent, empty, or a row with no
   * numbers at all prints nothing.
   */
  document_ranges?: Array<{
    /** Inv_Receipt_h.Code — 51 sale, 52 credit sale, 53 return, 83 refund. */
    code: number;
    count: number;
    cancelled_count: number;
    first_no: number | null;
    last_no: number | null;
  }>;
  /**
   * The counts behind the money, split the ways a manager checks them.
   *
   * Recomputed server-side from the shift's receipts on each read rather than
   * frozen at close, so historic shifts get it too. Null (or an older API
   * build omitting it) prints no breakdown sections at all — a blank block
   * says "not recorded", a block of zeroes says "nothing was sold".
   */
  breakdowns?: {
    by_order_type: ZReportBreakdownGroup[];
    by_payment_type: ZReportBreakdownGroup[];
    /** MONEY by payment method under the tenant's own names. Absent on an older API. */
    by_payment_method?: ZReportBreakdownGroup[];
    composition: ZReportBreakdownGroup[];
    by_salesman: ZReportBreakdownGroup[];
  } | null;
  cash_drawer: {
    expected_cash: number;
    counted_cash: number;
    variance: number;
  };
  denominations: Array<{ denomination: number; count: number; subtotal: number }>;
  /** Card / network count at close (migration 071). Absent or empty = none taken. */
  tender_counts?: Array<{ label: string; label_ar?: string | null; expected: number; counted: number; variance: number }>;
  adjustments: Array<{
    receipt_id: number;
    receipt_code: string | null;
    old_label: string | null;
    new_label: string | null;
    changed_by_name: string | null;
    approved_by_name: string | null;
    changed_at: string;
    reason: string | null;
  }>;
}

export interface BuildZReportHtmlOptions {
  /**
   * Stamped across the top instead of the REPRINT stripe. The admin console
   * passes "OFFICE COPY" — it prints from head office without bumping the
   * shift's print counter, so a page from there must not be mistakable for
   * the cashier's own till copy.
   */
  copyNote?: string;
  /**
   * `PrintShiftSerial` — print the shift's serial on the report. Legacy keeps
   * it off by default (and it is false on every tenant inspected), so the
   * default here matches: the serial is an internal reference the till staff
   * do not normally need on the paper.
   */
  showShiftSerial?: boolean;
  /**
   * Which paper the page is laid out for. `roll` (default) is the 80 mm
   * thermal band the close flow prints on. `a4` is a full sheet: the same
   * sections, boxed and flowed into two columns with sheet-sized type, for a
   * cashier reprinting a past shift from the Reports screen on the office
   * printer — on the roll stylesheet an A4 printer puts a 72 mm strip of small
   * text down the middle of an otherwise blank page.
   */
  paper?: 'roll' | 'a4';
  /**
   * The session language. 'ar' prints the WHOLE page in Arabic, right to left
   * — every heading and label, the breakdown rows under their Arabic names
   * (`label_ar`, else a fixed Arabic word for the order types and the
   * composition rows), dates with ص/م. 'en' (default) is the English page
   * exactly as before. The user, 2026-10-11: "Arabic when the language is
   * Arabic, English as it is when it is English, in all places".
   */
  lang?: 'en' | 'ar';
}

/**
 * Z-Report HTML for the hidden-iframe printer (Phase 10.10).
 *
 * Mirrors the receipt-print pipeline: outputs a complete <!DOCTYPE html>
 * document to be written into an iframe, then printReceipt() handles the
 * actual print. Structure: header → sales totals → orders → cash drawer →
 * denominations → adjustments.
 *
 * The float/pickup/drop/expense components stay in the snapshot for audit but
 * off the print — none of them feed expected cash. Receipt counts used to be
 * left off for the same reason and are now printed anyway: they are what a
 * manager checks the sales figure against ("42 orders for 1,200" is a sanity
 * check; "1,200" alone is not), and the person holding the page at close has
 * no other copy of them.
 *
 * They get their OWN section rather than a row among the sales, because every
 * row in that block is a currency amount and a bare integer sitting in the
 * same column reads as money.
 *
 * Lived in apps/cashier until the admin console gained its own Z-report
 * drill-down; moved here so both print the identical document.
 */
export function buildZReportHtml(
  z: ZReportPrintData,
  currency: string | { code: string; precision?: number } = 'EGP',
  options: BuildZReportHtmlOptions = {},
): string {
  const code = typeof currency === 'string' ? currency : currency.code;
  const precision = typeof currency === 'string' ? 2 : (currency.precision ?? 2);
  const digits = Math.max(0, Math.min(8, precision));
  const fmt = (n: number) => `${(Number.isFinite(n) ? n : 0).toFixed(digits)} ${code}`;
  // refunds_cash is stored as a positive magnitude; it reads as money leaving
  // the drawer, so the report shows it negative. Discounts get the same
  // treatment below — gross − discounts + VAT is the column that reaches net.
  const refunds = -z.sales.refunds_cash;
  const lang = options.lang ?? 'en';
  const ar = lang === 'ar';
  /** The label in the session's language. */
  const L = (en: string, arText: string): string => (ar ? arText : en);
  const date = (iso: string): string => formatDate(iso, lang);
  const stripe = options.copyNote
    ? `<div class="reprint-stripe">${escapeHtml(options.copyNote)}</div>`
    : z.printed_count > 1
      ? `<div class="reprint-stripe">REPRINT — إعادة طباعة (#${z.printed_count})</div>`
      : '';

  return `<!DOCTYPE html>
<html lang="${ar ? 'ar' : 'en'}" dir="${ar ? 'rtl' : 'ltr'}">
<head>
<meta charset="utf-8">
<title>${escapeHtml(z.header.shift_code)}</title>
<style>${options.paper === 'a4' ? Z_CSS_A4 : Z_CSS}</style>
</head>
<body>
${stripe}
<div class="z-header">
  <div class="z-title">${L('Z-Report', 'تقرير Z')}</div>
  ${options.showShiftSerial ? `<div class="z-shift-code">${escapeHtml(z.header.shift_code)}</div>` : ''}
  <div class="z-meta">
    ${escapeHtml(z.header.branch_name ?? '')}<br>
    ${L('Cashier', 'الكاشير')}: ${escapeHtml(z.header.cashier_name ?? '—')}<br>
    ${L('Drawer', 'الدرج')}: ${escapeHtml(z.header.drawer_name ?? '—')}<br>
    ${L('Opened', 'الفتح')}: ${escapeHtml(date(z.header.opened_at))}<br>
    ${L('Closed', 'الإغلاق')}: ${escapeHtml(z.header.closed_at ? date(z.header.closed_at) : '—')}${documentRangeHeaderLines(z.document_ranges, lang)}
  </div>
</div>

<div class="z-body">
<div class="divider"></div>
<div class="section">
  <div class="section-title">${L('Sales', 'المبيعات')}</div>
  ${methodRows(z, lang, fmt, row)}
  ${row(L('Refunds (cash)', 'مرتجعات نقدية'), fmt(refunds))}
  ${row(L('Gross sales', 'إجمالي المبيعات'), fmt(z.sales.gross_sales), true)}
  ${row(L('Discounts', 'الخصومات'), fmt(-z.sales.discounts_total))}
  ${row(L('VAT', 'ضريبة القيمة المضافة'), fmt(z.sales.vat_total))}
  ${row(L('Net sales', 'صافي المبيعات'), fmt(z.sales.net_sales), true)}
</div>

${z.receipts ? `
<div class="divider"></div>
<div class="section">
  <div class="section-title">${L('Orders', 'الطلبات')}</div>
  ${row(L('Orders', 'عدد الطلبات'), String(z.receipts.receipt_count))}
  ${z.receipts.voided_count > 0 ? row(L('Voided', 'ملغاة'), String(z.receipts.voided_count)) : ''}
  ${row(L('Average ticket', 'متوسط الفاتورة'), z.receipts.avg_ticket == null ? '—' : fmt(z.receipts.avg_ticket))}
</div>` : ''}

${breakdownSection(L('By order type', 'حسب نوع الطلب'), z.breakdowns?.by_order_type, fmt, { lang, arFallback: ORDER_TYPE_AR })}
${breakdownSection(L('By payment type', 'حسب طريقة الدفع'), z.breakdowns?.by_payment_type, fmt, { lang })}
${breakdownSection(L('Composition', 'تفاصيل الفواتير'), z.breakdowns?.composition, fmt, { lang, arFallback: COMPOSITION_AR })}
${/*
   * The salesman block prints its COUNT and no money, on purpose.
   *
   * Its figures are at a different grain from every other block on the page:
   * `SalesManId` lives on the line, so a salesman's money is the sum of their
   * own lines — gross, before the header discount and before VAT — while Sales
   * and both breakdowns above are header NET. On one real villashams shift
   * that is 8,732.800 against 6,975.162, and the gap is exactly the shift's
   * 1,757.638 of discounts. Two irreconcilable totals for one shift on one
   * page is how a close turns into an argument.
   *
   * The legacy POS reached the same conclusion about the same data: its own
   * per-salesman document (`buildSalesmanSummaryHtml`) prints the grouping and
   * no prices at all, "because the grouping is the whole point". The counts
   * are what was asked for here, and they are exact.
   */ ''}
${breakdownSection(L('By salesman', 'حسب البائع'), z.breakdowns?.by_salesman, fmt, {
  countNoun: L('receipts', 'فواتير'),
  money: false,
  lang,
})}

<div class="divider"></div>
<div class="section">
  <div class="section-title">${L('Cash drawer', 'درج النقدية')}</div>
  ${row(L('Expected cash', 'النقد المتوقع'), fmt(z.cash_drawer.expected_cash), true)}
  ${row(L('Counted cash', 'النقد المعدود'), fmt(z.cash_drawer.counted_cash), true)}
  ${row(L('Variance', 'الفرق'), fmt(z.cash_drawer.variance), true)}
</div>

${(z.tender_counts?.length ?? 0) > 0 ? `
<div class="divider"></div>
<div class="section">
  <div class="section-title">${L('Card / network count', 'عدّ البطاقات / الشبكة')}</div>
  ${z.tender_counts!.map((c) => [
    row((ar && c.label_ar?.trim()) || c.label, ''),
    row(L('  Expected', '  المتوقع'), fmt(c.expected)),
    row(L('  Counted', '  المعدود'), fmt(c.counted)),
    row(L('  Variance', '  الفرق'), fmt(c.variance), true),
  ].join('\n')).join('\n')}
</div>` : ''}

${z.denominations.length > 0 ? `
<div class="divider"></div>
<div class="section">
  <div class="section-title">${L('Denominations', 'الفئات النقدية')}</div>
  ${z.denominations.map((d) =>
    row(`${d.denomination.toFixed(digits)} × ${d.count}`, fmt(d.subtotal))
  ).join('\n')}
</div>` : ''}

${z.adjustments.length > 0 ? `
<div class="divider"></div>
<div class="section">
  <div class="section-title">${L('Adjustments (post-close)', 'تعديلات بعد الإغلاق')}</div>
  ${z.adjustments.map((a) => `
    <div class="adj-row">
      <div><b>${escapeHtml(a.receipt_code ?? `#${a.receipt_id}`)}</b> · ${escapeHtml(date(a.changed_at))}</div>
      <div class="muted">${escapeHtml(a.old_label ?? '—')} ${ar ? '←' : '→'} ${escapeHtml(a.new_label ?? '—')}</div>
      <div class="muted">${L('by', 'بواسطة')} ${escapeHtml(a.changed_by_name ?? '—')}${L(', approved by', '، اعتمدها')} ${escapeHtml(a.approved_by_name ?? '—')}</div>
      ${a.reason ? `<div class="muted">${L('reason', 'السبب')}: ${escapeHtml(a.reason)}</div>` : ''}
    </div>`).join('\n')}
</div>` : ''}
</div>

<div class="divider"></div>
<div class="footer">
  ${L('Generated', 'تاريخ الطباعة')} ${escapeHtml(date(z.generated_at))}
</div>
</body>
</html>`;
}

/** One entry of `ZReportPrintData.document_ranges`. */
export type ZReportDocumentRange = NonNullable<ZReportPrintData['document_ranges']>[number];

/**
 * What ONE document of each POS receipt code is called — singular, because it
 * is read inside a sentence: "Invoice #40310 to invoice #40312".
 *
 * `cap` is the form that opens the sentence and `low` the one that continues
 * it; Arabic has no case, so both are the same word there.
 */
const DOCUMENT_CODE_NOUN: Record<number, { cap: string; low: string; ar: string }> = {
  51: { cap: 'Invoice', low: 'invoice', ar: 'فاتورة' },
  52: { cap: 'Credit invoice', low: 'credit invoice', ar: 'فاتورة آجلة' },
  53: { cap: 'Return', low: 'return', ar: 'مرتجع' },
  83: { cap: 'Refund', low: 'refund', ar: 'استرداد' },
};

/**
 * The shift's span for one receipt code, as a sentence:
 *
 *   From invoice #40310 to invoice #40312   من فاتورة رقم 40310 إلى فاتورة رقم 40312
 *   Invoice #40313                          فاتورة رقم 40313
 *   From invoice #40308 to invoice #40309 (1 void)
 *
 * A shift that rang ONE document says "Invoice #40313" and not "from … to …"
 * of the same number: there is no span to walk, and printing one would read as
 * a mistake rather than as precision.
 *
 * Exported because the printed page, the till's Z-report modal and the admin
 * drawer must all say it the same way — it was three copies of a label helper
 * before this, which is three chances to drift.
 *
 * A cancelled receipt is named but stays INSIDE the span: it consumed its
 * number, so the paper sequence has no hole, and whoever checks the drawer
 * needs to know why one of the documents in that range is struck out.
 */
export function documentRangeSentence(
  r: ZReportDocumentRange,
  lang: 'en' | 'ar' = 'en',
): string {
  const noun = DOCUMENT_CODE_NOUN[r.code];
  const voidNote = r.cancelled_count > 0
    ? (lang === 'ar' ? ` (${r.cancelled_count} ملغاة)` : ` (${r.cancelled_count} void)`)
    : '';
  if (lang === 'ar') {
    const word = noun?.ar ?? `مستند ${r.code}`;
    return r.first_no === r.last_no
      ? `${word} رقم ${r.first_no}${voidNote}`
      : `من ${word} رقم ${r.first_no} إلى ${word} رقم ${r.last_no}${voidNote}`;
  }
  const cap = noun?.cap ?? `Code ${r.code}`;
  const low = noun?.low ?? `code ${r.code}`;
  return r.first_no === r.last_no
    ? `${cap} #${r.first_no}${voidNote}`
    : `From ${low} #${r.first_no} to ${low} #${r.last_no}${voidNote}`;
}

/**
 * "From invoice number X to invoice number Y" — the span a manager reconciles
 * the paper in the drawer against.
 *
 * One row per receipt code because each runs its OWN Entry_No sequence; the
 * rows are never collapsed into a single span (see the field's note). A code
 * whose numbers came back null — a tenant not stamping Entry_No — is dropped
 * rather than printed as "— → —", and a shift with one document prints that
 * one number instead of "40313 → 40313".
 *
 * Printed in the HEADER, on the line after "Closed", not as a section of its
 * own down the page: it identifies the document the way the branch and the
 * cashier do — "this page covers invoices 40310 to 40312" — and the person
 * reconciling the drawer wants it before the money, not after it.
 *
 * A sentence per receipt code rather than a label and a column of numbers: the
 * header has no columns, and in the body version the count sitting beside the
 * span read as one figure — a shift that rang a single receipt showed
 * "1  40313", which the tenant read as no range at all.
 */
function documentRangeHeaderLines(
  ranges: ZReportPrintData['document_ranges'],
  lang: 'en' | 'ar',
): string {
  const rows = (ranges ?? []).filter((r) => r.first_no != null && r.last_no != null);
  if (rows.length === 0) return '';
  return rows
    .map((r) => `<br>\n    <span dir="${lang === 'ar' ? 'rtl' : 'ltr'}">${escapeHtml(documentRangeSentence(r, lang))}</span>`)
    .join('');
}

/**
 * The Sales block's money by payment method, each under the tenant's own name
 * (Arabic when printing in Arabic and the type has one) — from
 * `breakdowns.by_payment_method` (the user, 2026-09-28: the fixed headings
 * printed khwada's "LINK" as "Wallet"). Falls back to the fixed Cash / Card /
 * Wallet / Credit / Other rows when an older API sent no such list.
 */
function methodRows(
  z: ZReportPrintData,
  lang: 'en' | 'ar',
  fmt: (n: number) => string,
  rowFn: (label: string, value: string) => string,
): string {
  const methods = z.breakdowns?.by_payment_method;
  if (methods && methods.length > 0) {
    return methods
      .map((m) => rowFn((lang === 'ar' && m.label_ar?.trim()) || m.label, fmt(m.total)))
      .join('\n  ');
  }
  const ar = lang === 'ar';
  return [
    rowFn(ar ? 'نقدي' : 'Cash', fmt(z.sales.cash)),
    rowFn(ar ? 'بطاقة' : 'Card', fmt(z.sales.card)),
    rowFn(ar ? 'محفظة' : 'Wallet', fmt(z.sales.wallet)),
    rowFn(ar ? 'آجل' : 'Credit', fmt(z.sales.credit)),
    z.sales.other ? rowFn(ar ? 'أخرى' : 'Other', fmt(z.sales.other)) : '',
  ].filter(Boolean).join('\n  ');
}

function row(label: string, value: string, bold = false): string {
  // dir="ltr" on the VALUE: "-5.000 KWD" must stay one run inside an Arabic
  // page, or the minus sign and the currency code drift to the wrong ends.
  return `<div class="row${bold ? ' bold' : ''}"><span class="l">${escapeHtml(label)}</span><span class="v" dir="ltr">${escapeHtml(value)}</span></div>`;
}

/**
 * One breakdown block: a label, its count, and its money, in three columns.
 *
 * The count gets a column of its own rather than being folded into the label
 * ("Delivery (31)") because the whole point of the block is scanning the
 * counts down the page. Right-aligning the money keeps it lined up with every
 * other amount on the ticket.
 *
 * A section with no rows is omitted entirely — a tenant that does not use
 * salesmen, or a shift that took no returns, should not be handed a heading
 * with nothing under it. `countNoun` names what is being counted where it is
 * not one-per-order: a salesman's number is DISTINCT RECEIPTS he appears on,
 * and two salesmen sharing a sale both count it, so those figures deliberately
 * do not add up to the shift's order count.
 */
function breakdownSection(
  title: string,
  groups: ZReportBreakdownGroup[] | undefined,
  fmt: (n: number) => string,
  opts: {
    countNoun?: string;
    money?: boolean;
    lang?: 'en' | 'ar';
    /** Arabic by group KEY, for an API build that sends no label_ar. */
    arFallback?: Record<string, string>;
  } = {},
): string {
  if (!groups || groups.length === 0) return '';
  const label = (g: ZReportBreakdownGroup): string =>
    opts.lang === 'ar' ? g.label_ar?.trim() || opts.arFallback?.[g.key] || g.label : g.label;
  const heading = opts.countNoun ? `${title} (${opts.countNoun})` : title;
  const money = opts.money !== false;
  return `
<div class="divider"></div>
<div class="section">
  <div class="section-title">${escapeHtml(heading)}</div>
  ${groups
    .map(
      (g) =>
        `<div class="row3">` +
        `<span class="l">${escapeHtml(label(g))}</span>` +
        `<span class="c">${escapeHtml(String(g.count))}</span>` +
        (money ? `<span class="v" dir="ltr">${escapeHtml(fmt(g.total))}</span>` : '') +
        `</div>`,
    )
    .join('\n')}
</div>`;
}

/** Arabic order-type names by breakdown key (Inv_Receipt_h.OrderType). */
const ORDER_TYPE_AR: Record<string, string> = {
  '1': 'سفري', '2': 'توصيل', '3': 'صالة', '4': 'استلام', '5': 'عضوية', null: 'بدون نوع',
};

/** Arabic composition rows by key. */
const COMPOSITION_AR: Record<string, string> = {
  sales: 'مبيعات', returns: 'مرتجعات', discounted: 'عليها خصم', voided: 'ملغاة',
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatDate(iso: string, lang: 'en' | 'ar' = 'en'): string {
  // Naive tenant-TZ strings from the backend must be parsed as components,
  // not via `new Date(...)` (which interprets them in host-local TZ).
  const naive = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d{1,7})?)?$/.exec(iso.trim());
  let y: number, m: number, day: number, hour24: number, mm: number;
  if (naive) {
    y = Number(naive[1]); m = Number(naive[2]); day = Number(naive[3]);
    hour24 = Number(naive[4]); mm = Number(naive[5]);
  } else {
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) return iso;
    const d = new Date(ms);
    y = d.getFullYear(); m = d.getMonth() + 1; day = d.getDate();
    hour24 = d.getHours(); mm = d.getMinutes();
  }
  const suffix = lang === 'ar' ? (hour24 >= 12 ? 'م' : 'ص') : hour24 >= 12 ? 'PM' : 'AM';
  const h12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(h12).padStart(2, '0')}:${String(mm).padStart(2, '0')} ${suffix}`;
}

/* Arabic page. Letter-spacing breaks the joins between Arabic letters, and
   uppercase means nothing there; Tahoma carries the Arabic glyphs on the
   Windows tills. Numbers keep their own right alignment (dir="ltr" spans). */
const Z_CSS_RTL = `
html[dir="rtl"] body { font-family: Tahoma, 'Segoe UI', Arial, sans-serif; }
html[dir="rtl"] .z-title,
html[dir="rtl"] .section-title { letter-spacing: 0; text-transform: none; }
`;

const Z_CSS = `
/* The PAGE is the printable band, as on the receipt: an 80mm head images only
   ~72mm and CLIPS the rest, and drivers disagree on where that band starts, so
   an 80mm page with margins lost text off BOTH edges (UNIQUEVISION 2026-10-27:
   labels' first letters on the left, "SA" on the right). A 72mm page with a
   symmetric 3mm inset leaves nothing to clip. */
@page { size: 72mm auto; margin: 0; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; width: 72mm; }
body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  padding: 4mm 3mm; color: #000; font-size: 12px;
}
.reprint-stripe {
  background: #000; color: #fff; text-align: center; padding: 4px;
  font-weight: bold; margin-bottom: 8px;
}
.z-header { text-align: center; margin-bottom: 8px; }
.z-title { font-size: 16px; font-weight: bold; }
.z-shift-code { font-size: 13px; margin: 2px 0; }
.z-meta { font-size: 11px; line-height: 1.4; }
.divider { border-top: 1px dashed #000; margin: 6px 0; }
.section { margin-bottom: 4px; }
.section-title {
  font-size: 11px; font-weight: bold; text-transform: uppercase;
  letter-spacing: 0.5px; margin-bottom: 4px;
}
.row { display: flex; justify-content: space-between; padding: 1px 0; }
/* Thermal heads print grey as a dither — labels in #333 came out pale and
   speckled next to the black values (Looz 2026-11-03). Text on the roll is #000. */
.row .l { color: #000; }
/* Three columns: label, count, money. The label is the only cell allowed to
   shrink and wrap — a long tenant payment-type name ("Visa / Mastercard POS")
   must not push the amount off the printable band, which on this head is
   silently CLIPPED rather than scaled. */
.row3 { display: flex; justify-content: space-between; align-items: baseline; gap: 6px; padding: 1px 0; }
.row3 .l { flex: 1 1 auto; min-width: 0; color: #000; overflow-wrap: anywhere; }
.row3 .c { flex: 0 0 auto; text-align: right; min-width: 22px; }
.row3 .v { flex: 0 0 auto; text-align: right; }
.row.bold { font-weight: bold; border-top: 1px dashed #000; padding-top: 3px; margin-top: 3px; }
.adj-row { font-size: 11px; padding: 4px 0; border-bottom: 1px dotted #ccc; }
.adj-row .muted { color: #000; font-size: 10px; }
.footer { text-align: center; font-size: 10px; color: #000; margin-top: 8px; }
${Z_CSS_RTL}
`;

/* A4 sheet: the same markup as the roll, restyled. Sections become boxed
   cards flowed into two columns; the dashed dividers that separate blocks on
   the roll are dropped because the boxes already do that job. Type is sized
   for a sheet read at arm's length, not a slip read at the till. */
const Z_CSS_A4 = `
@page { size: A4; margin: 15mm 14mm; }
* { box-sizing: border-box; }
body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Tahoma, Arial, sans-serif;
  margin: 0; padding: 0; color: #000; font-size: 13px; line-height: 1.4;
}
.reprint-stripe {
  background: #000; color: #fff; text-align: center; padding: 6px;
  font-weight: bold; margin-bottom: 12px; font-size: 13px;
}
.z-header { text-align: center; margin-bottom: 14px; padding-bottom: 10px; border-bottom: 2px solid #000; }
.z-title { font-size: 24px; font-weight: bold; letter-spacing: 1px; }
.z-shift-code { font-size: 15px; margin: 4px 0; }
.z-meta { font-size: 13px; line-height: 1.5; color: #222; }
.z-body { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; align-items: start; }
.z-body > .divider { display: none; }
.section {
  border: 1px solid #000; border-radius: 4px; padding: 8px 12px 10px;
  margin: 0; break-inside: avoid;
}
.section-title {
  font-size: 12px; font-weight: bold; text-transform: uppercase;
  letter-spacing: 0.8px; margin: 0 0 6px; padding-bottom: 4px; border-bottom: 1px solid #000;
}
.row { display: flex; justify-content: space-between; gap: 12px; padding: 2px 0; }
.row .l { color: #333; }
.row .v { font-variant-numeric: tabular-nums; white-space: nowrap; }
.row3 { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; padding: 2px 0; }
.row3 .l { flex: 1 1 auto; min-width: 0; color: #333; overflow-wrap: anywhere; }
.row3 .c { flex: 0 0 auto; text-align: right; min-width: 32px; font-variant-numeric: tabular-nums; }
.row3 .v { flex: 0 0 auto; text-align: right; min-width: 110px; font-variant-numeric: tabular-nums; white-space: nowrap; }
.row.bold { font-weight: bold; border-top: 1px solid #000; padding-top: 4px; margin-top: 4px; }
.adj-row { font-size: 12px; padding: 5px 0; border-bottom: 1px dotted #999; }
.adj-row .muted { color: #444; font-size: 11px; }
.divider { border-top: 1px solid #000; margin: 14px 0 8px; }
.footer { text-align: center; font-size: 11px; color: #555; }
${Z_CSS_RTL}
`;
